import { Artifact, Book, Project } from '../../services/platformApi';
import {
  BookType,
  ManuscriptV2,
  createManuscriptV2,
  createFlowSection,
  ensureV2,
  paragraphBlocksFromText,
} from './manuscriptSchema';

export type BookFontRole =
  | 'regular_text'
  | 'chapter_title'
  | 'sub_chapter'
  | 'sub_sub_chapter'
  | 'quoted_text';

export interface BookFontFormState {
  family: string;
  sizePt: string;
  bold: boolean;
  italic: boolean;
  color: string;
  lineHeight: string;
}

export interface BookImagePlacementFormState {
  id: string;
  artifactId: string;
  anchorParagraphId: string;
  placementMode: string;
  pageSide: string;
  xPercent: string;
  yPercent: string;
  widthPercent: string;
  heightPercent: string;
  alignment: string;
  rotationDegrees: string;
  filter: string;
  cropTopPercent: string;
  cropRightPercent: string;
  cropBottomPercent: string;
  cropLeftPercent: string;
  caption: string;
}

export interface BookCoverItemFormState {
  id: string;
  side: 'front' | 'back';
  itemType: 'photo' | 'text';
  artifactId: string;
  text: string;
  xPercent: string;
  yPercent: string;
  widthPercent: string;
  heightPercent: string;
  rotationDegrees: string;
  filter: string;
  cropTopPercent: string;
  cropRightPercent: string;
  cropBottomPercent: string;
  cropLeftPercent: string;
  // Text-item styling (ignored for photo items).
  fontFamily: string;
  fontSizePt: string;
  color: string;
  bold: boolean;
  italic: boolean;
  textAlign: string;
}

export interface BookDesignFormState {
  sourceArtifactId: string;
  createdByUserId: string;
  title: string;
  subtitle: string;
  authorName: string;
  language: string;
  textDirection: string;
  textAlignment: string;
  pageWidthCm: string;
  pageHeightCm: string;
  marginInnerCm: string;
  marginOuterCm: string;
  marginTopCm: string;
  marginBottomCm: string;
  rightHeaderTopMarginCm: string;
  rightHeaderAlignment: string;
  rightHeaderContent: string;
  leftHeaderTopMarginCm: string;
  leftHeaderAlignment: string;
  leftHeaderContent: string;
  startPageNumber: string;
  paragraphIndentCm: string;
  subParagraphIndentCm: string;
  subSubParagraphIndentCm: string;
  spacing: {
    chapterTitleBeforeCm: string;
    chapterTitleAfterCm: string;
    subChapterBeforeCm: string;
    subChapterAfterCm: string;
    subSubChapterBeforeCm: string;
    subSubChapterAfterCm: string;
    textBeforeCm: string;
    textAfterCm: string;
  };
  fonts: Record<BookFontRole, BookFontFormState>;
  paragraphEndMarkerEnabled: boolean;
  paragraphEndMarker: string;
  paragraphEndMarkerOffsetCm: string;
  flagSingleWordLastLine: boolean;
  flagSingleLinePage: boolean;
  chapterStartSide: string;
  tocEnabled: boolean;
  tocTitle: string;
  tocIncludeSubChapters: boolean;
  tocIncludeSubSubChapters: boolean;
  tocDotLeaders: boolean;
  defaultBackgroundEnabled: boolean;
  defaultBackgroundMode: string;
  defaultBackgroundPlacement: string;
  defaultBackgroundAllArtifactId: string;
  defaultBackgroundLeftArtifactId: string;
  defaultBackgroundRightArtifactId: string;
  bookType: BookType;
  // Structured page/block manuscript (schema v2). Authored by the FlowEditor and
  // the source of truth on save. `manuscriptText`/`imagePlacements` remain for
  // the legacy read-only paragraph summary and v1 books loaded before migration.
  manuscript: ManuscriptV2;
  manuscriptText: string;
  imagePlacements: BookImagePlacementFormState[];
  coverItems: BookCoverItemFormState[];
  photoArtifactId: string;
  photoAnchorParagraphId: string;
  photoPlacementMode: string;
  photoPageSide: string;
  photoXPercent: string;
  photoYPercent: string;
  photoWidthPercent: string;
  photoHeightPercent: string;
  photoAlignment: string;
  photoRotationDegrees: string;
  photoFilter: string;
  photoCropTopPercent: string;
  photoCropRightPercent: string;
  photoCropBottomPercent: string;
  photoCropLeftPercent: string;
  photoCaption: string;
  coverSide: 'front' | 'back';
  coverItemType: 'photo' | 'text';
  coverPhotoArtifactId: string;
  coverText: string;
  coverXPercent: string;
  coverYPercent: string;
  coverWidthPercent: string;
  coverHeightPercent: string;
  coverRotationDegrees: string;
  coverFilter: string;
  coverCropTopPercent: string;
  coverCropRightPercent: string;
  coverCropBottomPercent: string;
  coverCropLeftPercent: string;
  coverFontFamily: string;
  coverFontSizePt: string;
  coverColor: string;
  coverBold: boolean;
  coverItalic: boolean;
  coverTextAlign: string;
  coverFrontBackgroundColor: string;
  coverBackBackgroundColor: string;
  versionNote: string;
  exportType: string;
  publishingProvider: string;
  publishingNotes: string;
}

const DEFAULT_FONTS: Record<BookFontRole, BookFontFormState> = {
  regular_text: {
    family: 'Noto Serif',
    sizePt: '11',
    bold: false,
    italic: false,
    color: '#111111',
    lineHeight: '1.4',
  },
  chapter_title: {
    family: 'Noto Serif',
    sizePt: '20',
    bold: true,
    italic: false,
    color: '#111111',
    lineHeight: '1.2',
  },
  sub_chapter: {
    family: 'Noto Serif',
    sizePt: '15',
    bold: true,
    italic: false,
    color: '#111111',
    lineHeight: '1.25',
  },
  sub_sub_chapter: {
    family: 'Noto Serif',
    sizePt: '13',
    bold: true,
    italic: false,
    color: '#111111',
    lineHeight: '1.3',
  },
  quoted_text: {
    family: 'Noto Serif',
    sizePt: '11',
    bold: false,
    italic: true,
    color: '#111111',
    lineHeight: '1.4',
  },
};

export const bookFontLabels: Record<BookFontRole, string> = {
  regular_text: 'Regular Text',
  chapter_title: 'Chapter Title',
  sub_chapter: 'Sub Chapter',
  sub_sub_chapter: 'Sub Sub Chapter',
  quoted_text: 'Quoted Text',
};

export const bookFontRoles: BookFontRole[] = [
  'regular_text',
  'chapter_title',
  'sub_chapter',
  'sub_sub_chapter',
  'quoted_text',
];

const asRecord = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
};

const asArray = (value: unknown): unknown[] => {
  return Array.isArray(value) ? value : [];
};

const toStringValue = (value: unknown, fallback: string): string => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return String(value);
};

const toBooleanValue = (value: unknown, fallback: boolean): boolean => {
  return typeof value === 'boolean' ? value : fallback;
};

const toNumber = (value: string, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const manuscriptFromSourceArtifact = (sourceArtifact?: Artifact): ManuscriptV2 => {
  const structured = asRecord(sourceArtifact?.metadata_json?.extracted_manuscript_json);
  if (Object.keys(structured).length > 0) {
    return ensureV2(structured, 'text');
  }
  const blocks = paragraphBlocksFromText(sourceArtifact?.text_content || '');
  const section = createFlowSection(sourceArtifact?.title || 'Chapter One');
  return createManuscriptV2('text', [
    blocks.length > 0 ? { ...section, blocks } : section,
  ]);
};

const createDefaultForm = (project?: Project | null, sourceArtifact?: Artifact): BookDesignFormState => {
  const direction = project?.text_direction || 'ltr';
  const alignment = direction === 'rtl' ? 'right' : 'left';
  return {
    sourceArtifactId: sourceArtifact?.id.toString() || '',
    createdByUserId: project?.owner_id?.toString() || '',
    title: sourceArtifact?.title || project?.title || '',
    subtitle: '',
    authorName: '',
    language: project?.primary_language_code || 'en',
    textDirection: direction,
    textAlignment: alignment,
    pageWidthCm: '15.24',
    pageHeightCm: '22.86',
    marginInnerCm: '2.2',
    marginOuterCm: '1.8',
    marginTopCm: '2',
    marginBottomCm: '2',
    rightHeaderTopMarginCm: '1.2',
    rightHeaderAlignment: 'outer',
    rightHeaderContent: '{page_number} | {author_name}',
    leftHeaderTopMarginCm: '1.2',
    leftHeaderAlignment: 'outer',
    leftHeaderContent: '{book_title} | {page_number}',
    startPageNumber: '1',
    paragraphIndentCm: '0.5',
    subParagraphIndentCm: '0.9',
    subSubParagraphIndentCm: '1.2',
    spacing: {
      chapterTitleBeforeCm: '1.2',
      chapterTitleAfterCm: '0.8',
      subChapterBeforeCm: '0.8',
      subChapterAfterCm: '0.4',
      subSubChapterBeforeCm: '0.6',
      subSubChapterAfterCm: '0.3',
      textBeforeCm: '0',
      textAfterCm: '0.35',
    },
    fonts: { ...DEFAULT_FONTS },
    paragraphEndMarkerEnabled: false,
    paragraphEndMarker: '***',
    paragraphEndMarkerOffsetCm: '0.8',
    flagSingleWordLastLine: true,
    flagSingleLinePage: true,
    chapterStartSide: 'left',
    tocEnabled: true,
    tocTitle: '',
    tocIncludeSubChapters: true,
    tocIncludeSubSubChapters: false,
    tocDotLeaders: true,
    defaultBackgroundEnabled: false,
    defaultBackgroundMode: 'all',
    defaultBackgroundPlacement: 'cover',
    defaultBackgroundAllArtifactId: '',
    defaultBackgroundLeftArtifactId: '',
    defaultBackgroundRightArtifactId: '',
    bookType: 'text',
    manuscript: manuscriptFromSourceArtifact(sourceArtifact),
    manuscriptText: sourceArtifact?.text_content || '',
    imagePlacements: [],
    coverItems: [],
    photoArtifactId: '',
    photoAnchorParagraphId: 'paragraph-1',
    photoPlacementMode: 'after_paragraph',
    photoPageSide: 'next',
    photoXPercent: '50',
    photoYPercent: '20',
    photoWidthPercent: '60',
    photoHeightPercent: '35',
    photoAlignment: 'center',
    photoRotationDegrees: '0',
    photoFilter: 'none',
    photoCropTopPercent: '0',
    photoCropRightPercent: '0',
    photoCropBottomPercent: '0',
    photoCropLeftPercent: '0',
    photoCaption: '',
    coverSide: 'front',
    coverItemType: 'photo',
    coverPhotoArtifactId: '',
    coverText: '',
    coverXPercent: '50',
    coverYPercent: '50',
    coverWidthPercent: '70',
    coverHeightPercent: '50',
    coverRotationDegrees: '0',
    coverFilter: 'none',
    coverCropTopPercent: '0',
    coverCropRightPercent: '0',
    coverCropBottomPercent: '0',
    coverCropLeftPercent: '0',
    coverFontFamily: '',
    coverFontSizePt: '',
    coverColor: '',
    coverBold: true,
    coverItalic: false,
    coverTextAlign: 'center',
    coverFrontBackgroundColor: '',
    coverBackBackgroundColor: '',
    versionNote: '',
    exportType: 'print_pdf',
    publishingProvider: 'amazon_kdp',
    publishingNotes: '',
  };
};

export const initialBookDesignForm = createDefaultForm();

export const extractManuscriptText = (manuscript: Record<string, unknown>): string => {
  const chapters = asArray(manuscript.chapters);
  return chapters
    .map((chapterValue) => {
      const chapter = asRecord(chapterValue);
      const paragraphs = asArray(chapter.paragraphs)
        .map((paragraphValue) => {
          const paragraph = asRecord(paragraphValue);
          return toStringValue(paragraph.text, '').trim();
        })
        .filter(Boolean);
      return paragraphs.join('\n\n');
    })
    .filter(Boolean)
    .join('\n\n');
};

export const extractImagePlacements = (manuscript: Record<string, unknown>): BookImagePlacementFormState[] => {
  const chapters = asArray(manuscript.chapters);
  const firstChapter = asRecord(chapters[0]);
  return asArray(firstChapter.image_placements).map((placementValue, index) => {
    const placement = asRecord(placementValue);
    return {
      id: toStringValue(placement.id, `image-placement-${index + 1}`),
      artifactId: toStringValue(placement.artifact_id, ''),
      anchorParagraphId: toStringValue(placement.anchor_paragraph_id, 'paragraph-1'),
      placementMode: toStringValue(placement.placement_mode || placement.placement, 'after_paragraph'),
      pageSide: toStringValue(placement.page_side, 'next'),
      xPercent: toStringValue(placement.x_percent, '50'),
      yPercent: toStringValue(placement.y_percent, '20'),
      widthPercent: toStringValue(placement.width_percent, '60'),
      heightPercent: toStringValue(placement.height_percent, '35'),
      alignment: toStringValue(placement.alignment, 'center'),
      rotationDegrees: toStringValue(placement.rotation_degrees, '0'),
      filter: toStringValue(placement.filter, 'none'),
      cropTopPercent: toStringValue(placement.crop_top_percent, '0'),
      cropRightPercent: toStringValue(placement.crop_right_percent, '0'),
      cropBottomPercent: toStringValue(placement.crop_bottom_percent, '0'),
      cropLeftPercent: toStringValue(placement.crop_left_percent, '0'),
      caption: toStringValue(placement.caption, ''),
    };
  });
};

export const extractCoverItems = (cover: Record<string, unknown> | null | undefined): BookCoverItemFormState[] => {
  const coverRecord = asRecord(cover);
  return asArray(coverRecord.items).map((itemValue, index) => {
    const item = asRecord(itemValue);
    const side = toStringValue(item.side, 'front') === 'back' ? 'back' : 'front';
    const itemType = toStringValue(item.item_type, 'photo') === 'text' ? 'text' : 'photo';
    return {
      id: toStringValue(item.id, `cover-item-${index + 1}`),
      side,
      itemType,
      artifactId: toStringValue(item.artifact_id, ''),
      text: toStringValue(item.text, ''),
      xPercent: toStringValue(item.x_percent, '50'),
      yPercent: toStringValue(item.y_percent, '50'),
      widthPercent: toStringValue(item.width_percent, '70'),
      heightPercent: toStringValue(item.height_percent, '50'),
      rotationDegrees: toStringValue(item.rotation_degrees, '0'),
      filter: toStringValue(item.filter, 'none'),
      cropTopPercent: toStringValue(item.crop_top_percent, '0'),
      cropRightPercent: toStringValue(item.crop_right_percent, '0'),
      cropBottomPercent: toStringValue(item.crop_bottom_percent, '0'),
      cropLeftPercent: toStringValue(item.crop_left_percent, '0'),
      fontFamily: toStringValue(item.font_family, ''),
      fontSizePt: toStringValue(item.font_size_pt, ''),
      color: toStringValue(item.color, ''),
      bold: item.bold === undefined ? true : Boolean(item.bold),
      italic: Boolean(item.italic),
      textAlign: toStringValue(item.text_align, 'center'),
    };
  });
};

export const createBookDesignForm = (
  project?: Project | null,
  sourceArtifact?: Artifact
): BookDesignFormState => createDefaultForm(project, sourceArtifact);

// Per-book_type trim/margin seeds (P4). Text/mixed keep the trade-paperback
// default; picture books default to a square trim with tight, even margins.
// Applied when the user picks a book type, so choosing "picture" reshapes the
// page the way a picture-book layout expects.
const BOOK_TYPE_GEOMETRY: Record<BookType, Partial<BookDesignFormState>> = {
  text: {
    pageWidthCm: '15.24',
    pageHeightCm: '22.86',
    marginInnerCm: '2.2',
    marginOuterCm: '1.8',
    marginTopCm: '2',
    marginBottomCm: '2',
  },
  mixed: {
    pageWidthCm: '15.24',
    pageHeightCm: '22.86',
    marginInnerCm: '2.2',
    marginOuterCm: '1.8',
    marginTopCm: '2',
    marginBottomCm: '2',
  },
  picture: {
    pageWidthCm: '21',
    pageHeightCm: '21',
    marginInnerCm: '1.2',
    marginOuterCm: '1.2',
    marginTopCm: '1.2',
    marginBottomCm: '1.2',
  },
};

export const applyBookTypeDefaults = (
  form: BookDesignFormState,
  bookType: BookType
): BookDesignFormState => ({
  ...form,
  ...BOOK_TYPE_GEOMETRY[bookType],
  bookType,
  manuscript: { ...form.manuscript, book_type: bookType },
});

// Overlays a design_settings_json blob onto an existing form, leaving
// manuscript/cover/photo/publishing fields untouched. Shared by loading a
// book's own settings (bookToDesignForm) and applying a design template onto
// the form the user is currently editing (DesignPublishingTab).
export const applyDesignSettingsToForm = (
  form: BookDesignFormState,
  designSettings: Record<string, unknown>,
  overrides?: { language?: string; textDirection?: string }
): BookDesignFormState => {
  const settings = asRecord(designSettings);
  const pageSize = asRecord(settings.page_size);
  const margins = asRecord(settings.margins);
  const headers = asRecord(settings.headers);
  const rightHeader = asRecord(headers.right_page);
  const leftHeader = asRecord(headers.left_page);
  const paging = asRecord(settings.paging);
  const indentation = asRecord(settings.indentation);
  const spacing = asRecord(settings.vertical_spacing);
  const layoutRules = asRecord(settings.layout_rules);
  const paragraphMarker = asRecord(layoutRules.paragraph_end_marker);
  const tableOfContents = asRecord(settings.table_of_contents);
  const tocLevels = Array.isArray(tableOfContents.levels) ? tableOfContents.levels.map(String) : [];
  const defaultBackground = asRecord(settings.default_background);
  const defaultBgAll = asRecord(defaultBackground.all);
  const defaultBgLeft = asRecord(defaultBackground.left);
  const defaultBgRight = asRecord(defaultBackground.right);
  const fontSettings = asRecord(settings.fonts);
  const fonts = bookFontRoles.reduce<Record<BookFontRole, BookFontFormState>>((accumulator, role) => {
    const currentFont = asRecord(fontSettings[role]);
    const defaultFont = form.fonts[role];
    accumulator[role] = {
      family: toStringValue(currentFont.family, defaultFont.family),
      sizePt: toStringValue(currentFont.size_pt, defaultFont.sizePt),
      bold: toBooleanValue(currentFont.bold, defaultFont.bold),
      italic: toBooleanValue(currentFont.italic, defaultFont.italic),
      color: toStringValue(currentFont.color, defaultFont.color),
      lineHeight: toStringValue(currentFont.line_height, defaultFont.lineHeight),
    };
    return accumulator;
  }, {} as Record<BookFontRole, BookFontFormState>);

  return {
    ...form,
    language: overrides?.language || form.language,
    textDirection: overrides?.textDirection || form.textDirection,
    textAlignment: toStringValue(settings.text_alignment, form.textAlignment),
    pageWidthCm: toStringValue(pageSize.width_cm, form.pageWidthCm),
    pageHeightCm: toStringValue(pageSize.height_cm, form.pageHeightCm),
    marginInnerCm: toStringValue(margins.inner_cm, form.marginInnerCm),
    marginOuterCm: toStringValue(margins.outer_cm, form.marginOuterCm),
    marginTopCm: toStringValue(margins.top_cm, form.marginTopCm),
    marginBottomCm: toStringValue(margins.bottom_cm, form.marginBottomCm),
    rightHeaderTopMarginCm: toStringValue(rightHeader.top_margin_cm, form.rightHeaderTopMarginCm),
    rightHeaderAlignment: toStringValue(rightHeader.alignment, form.rightHeaderAlignment),
    rightHeaderContent: toStringValue(rightHeader.content, form.rightHeaderContent),
    leftHeaderTopMarginCm: toStringValue(leftHeader.top_margin_cm, form.leftHeaderTopMarginCm),
    leftHeaderAlignment: toStringValue(leftHeader.alignment, form.leftHeaderAlignment),
    leftHeaderContent: toStringValue(leftHeader.content, form.leftHeaderContent),
    startPageNumber: toStringValue(paging.start_page_number, form.startPageNumber),
    paragraphIndentCm: toStringValue(indentation.paragraph_cm, form.paragraphIndentCm),
    subParagraphIndentCm: toStringValue(indentation.sub_paragraph_cm, form.subParagraphIndentCm),
    subSubParagraphIndentCm: toStringValue(indentation.sub_sub_paragraph_cm, form.subSubParagraphIndentCm),
    spacing: {
      chapterTitleBeforeCm: toStringValue(spacing.chapter_title_before_cm, form.spacing.chapterTitleBeforeCm),
      chapterTitleAfterCm: toStringValue(spacing.chapter_title_after_cm, form.spacing.chapterTitleAfterCm),
      subChapterBeforeCm: toStringValue(spacing.sub_chapter_before_cm, form.spacing.subChapterBeforeCm),
      subChapterAfterCm: toStringValue(spacing.sub_chapter_after_cm, form.spacing.subChapterAfterCm),
      subSubChapterBeforeCm: toStringValue(spacing.sub_sub_chapter_before_cm, form.spacing.subSubChapterBeforeCm),
      subSubChapterAfterCm: toStringValue(spacing.sub_sub_chapter_after_cm, form.spacing.subSubChapterAfterCm),
      textBeforeCm: toStringValue(spacing.text_before_cm, form.spacing.textBeforeCm),
      textAfterCm: toStringValue(spacing.text_after_cm, form.spacing.textAfterCm),
    },
    fonts,
    paragraphEndMarkerEnabled: toBooleanValue(paragraphMarker.enabled, form.paragraphEndMarkerEnabled),
    paragraphEndMarker: toStringValue(paragraphMarker.marker, form.paragraphEndMarker),
    paragraphEndMarkerOffsetCm: toStringValue(paragraphMarker.offset_below_cm, form.paragraphEndMarkerOffsetCm),
    flagSingleWordLastLine: toBooleanValue(layoutRules.flag_single_word_last_line, form.flagSingleWordLastLine),
    flagSingleLinePage: toBooleanValue(layoutRules.flag_single_line_page, form.flagSingleLinePage),
    chapterStartSide: toStringValue(layoutRules.chapter_start_side, form.chapterStartSide),
    tocEnabled: toBooleanValue(tableOfContents.enabled, form.tocEnabled),
    tocTitle: toStringValue(tableOfContents.title, form.tocTitle),
    tocIncludeSubChapters: tocLevels.length > 0 ? tocLevels.includes('sub_chapter') : form.tocIncludeSubChapters,
    tocIncludeSubSubChapters: tocLevels.length > 0 ? tocLevels.includes('sub_sub_chapter') : form.tocIncludeSubSubChapters,
    tocDotLeaders: toBooleanValue(tableOfContents.dot_leaders, form.tocDotLeaders),
    defaultBackgroundEnabled: toBooleanValue(defaultBackground.enabled, form.defaultBackgroundEnabled),
    defaultBackgroundMode: toStringValue(defaultBackground.mode, form.defaultBackgroundMode),
    defaultBackgroundPlacement: toStringValue(defaultBackground.placement, form.defaultBackgroundPlacement),
    defaultBackgroundAllArtifactId: toStringValue(defaultBgAll.artifact_id, form.defaultBackgroundAllArtifactId),
    defaultBackgroundLeftArtifactId: toStringValue(defaultBgLeft.artifact_id, form.defaultBackgroundLeftArtifactId),
    defaultBackgroundRightArtifactId: toStringValue(defaultBgRight.artifact_id, form.defaultBackgroundRightArtifactId),
  };
};

export const bookToDesignForm = (
  book: Book,
  project?: Project | null,
  sourceArtifact?: Artifact
): BookDesignFormState => {
  const defaults = createDefaultForm(project, sourceArtifact);
  const withDesignSettings = applyDesignSettingsToForm(
    {
      ...defaults,
      sourceArtifactId: book.source_artifact_id?.toString() || defaults.sourceArtifactId,
      title: book.title,
      subtitle: book.subtitle || '',
      authorName: book.author_name || '',
    },
    book.design_settings_json,
    { language: book.language || defaults.language, textDirection: book.text_direction || defaults.textDirection }
  );

  const bookType = (book.book_type as BookType) || 'text';
  const coverBackground = asRecord(asRecord(book.cover_json).background_color);
  return {
    ...withDesignSettings,
    bookType,
    manuscript: ensureV2(book.manuscript_json, bookType),
    manuscriptText: extractManuscriptText(book.manuscript_json) || defaults.manuscriptText,
    imagePlacements: extractImagePlacements(book.manuscript_json),
    coverItems: extractCoverItems(book.cover_json),
    coverFrontBackgroundColor: toStringValue(coverBackground.front, ''),
    coverBackBackgroundColor: toStringValue(coverBackground.back, ''),
  };
};

export const buildBookDesignSettings = (form: BookDesignFormState): Record<string, unknown> => ({
  text_alignment: form.textAlignment,
  page_size: {
    width_cm: toNumber(form.pageWidthCm, 15.24),
    height_cm: toNumber(form.pageHeightCm, 22.86),
  },
  margins: {
    inner_cm: toNumber(form.marginInnerCm, 2.2),
    outer_cm: toNumber(form.marginOuterCm, 1.8),
    top_cm: toNumber(form.marginTopCm, 2),
    bottom_cm: toNumber(form.marginBottomCm, 2),
  },
  headers: {
    right_page: {
      top_margin_cm: toNumber(form.rightHeaderTopMarginCm, 1.2),
      alignment: form.rightHeaderAlignment,
      content: form.rightHeaderContent,
    },
    left_page: {
      top_margin_cm: toNumber(form.leftHeaderTopMarginCm, 1.2),
      alignment: form.leftHeaderAlignment,
      content: form.leftHeaderContent,
    },
  },
  fonts: bookFontRoles.reduce<Record<string, unknown>>((accumulator, role) => {
    const font = form.fonts[role];
    accumulator[role] = {
      family: font.family,
      size_pt: toNumber(font.sizePt, Number(DEFAULT_FONTS[role].sizePt)),
      bold: font.bold,
      italic: font.italic,
      color: font.color,
      line_height: toNumber(font.lineHeight, Number(DEFAULT_FONTS[role].lineHeight)),
    };
    return accumulator;
  }, {}),
  paging: {
    start_page_number: toNumber(form.startPageNumber, 1),
    show_page_numbers: true,
  },
  indentation: {
    paragraph_cm: toNumber(form.paragraphIndentCm, 0.5),
    sub_paragraph_cm: toNumber(form.subParagraphIndentCm, 0.9),
    sub_sub_paragraph_cm: toNumber(form.subSubParagraphIndentCm, 1.2),
  },
  vertical_spacing: {
    chapter_title_before_cm: toNumber(form.spacing.chapterTitleBeforeCm, 1.2),
    chapter_title_after_cm: toNumber(form.spacing.chapterTitleAfterCm, 0.8),
    sub_chapter_before_cm: toNumber(form.spacing.subChapterBeforeCm, 0.8),
    sub_chapter_after_cm: toNumber(form.spacing.subChapterAfterCm, 0.4),
    sub_sub_chapter_before_cm: toNumber(form.spacing.subSubChapterBeforeCm, 0.6),
    sub_sub_chapter_after_cm: toNumber(form.spacing.subSubChapterAfterCm, 0.3),
    text_before_cm: toNumber(form.spacing.textBeforeCm, 0),
    text_after_cm: toNumber(form.spacing.textAfterCm, 0.35),
  },
  layout_rules: {
    paragraph_end_marker: {
      enabled: form.paragraphEndMarkerEnabled,
      marker: form.paragraphEndMarker || '***',
      offset_below_cm: toNumber(form.paragraphEndMarkerOffsetCm, 0.8),
      alignment: 'center',
    },
    flag_single_word_last_line: form.flagSingleWordLastLine,
    flag_single_line_page: form.flagSingleLinePage,
    chapter_start_side: form.chapterStartSide,
  },
  table_of_contents: {
    enabled: form.tocEnabled,
    title: form.tocTitle.trim() || null,
    placement: 'after_cover',
    levels: [
      'chapter',
      ...(form.tocIncludeSubChapters ? ['sub_chapter'] : []),
      ...(form.tocIncludeSubSubChapters ? ['sub_sub_chapter'] : []),
    ],
    dot_leaders: form.tocDotLeaders,
  },
  default_background: {
    enabled: form.defaultBackgroundEnabled,
    mode: form.defaultBackgroundMode,
    placement: form.defaultBackgroundPlacement,
    all: { artifact_id: toNumber(form.defaultBackgroundAllArtifactId, 0) || null },
    left: { artifact_id: toNumber(form.defaultBackgroundLeftArtifactId, 0) || null },
    right: { artifact_id: toNumber(form.defaultBackgroundRightArtifactId, 0) || null },
  },
  language: form.language,
  text_direction: form.textDirection,
});

// Serialize the structured v2 manuscript authored by the FlowEditor. book_type
// is synced from the form so BookModel.book_type (the source of truth) and the
// manuscript agree. front/back matter and any non-flow units (fixed page/spread
// canvases from picture books) are preserved untouched.
export const buildBookManuscript = (
  form: BookDesignFormState,
  _currentBook?: Book | null
): Record<string, unknown> => ({
  ...form.manuscript,
  book_type: form.bookType,
});

export const buildBookCover = (form: BookDesignFormState): Record<string, unknown> => ({
  version: 1,
  background_color: {
    front: form.coverFrontBackgroundColor || null,
    back: form.coverBackBackgroundColor || null,
  },
  items: form.coverItems.map((item, index) => ({
    id: item.id || `cover-item-${index + 1}`,
    side: item.side,
    item_type: item.itemType,
    artifact_id: item.artifactId ? toNumber(item.artifactId, 0) : null,
    text: item.text,
    x_percent: toNumber(item.xPercent, 50),
    y_percent: toNumber(item.yPercent, 50),
    width_percent: toNumber(item.widthPercent, 70),
    height_percent: toNumber(item.heightPercent, 50),
    rotation_degrees: toNumber(item.rotationDegrees, 0),
    filter: item.filter || 'none',
    crop_top_percent: toNumber(item.cropTopPercent, 0),
    crop_right_percent: toNumber(item.cropRightPercent, 0),
    crop_bottom_percent: toNumber(item.cropBottomPercent, 0),
    crop_left_percent: toNumber(item.cropLeftPercent, 0),
    // Text-item styling; harmless on photo items (renderer ignores it there).
    ...(item.itemType === 'text'
      ? {
          font_family: item.fontFamily || null,
          font_size_pt: item.fontSizePt ? toNumber(item.fontSizePt, 22) : null,
          color: item.color || null,
          bold: item.bold,
          italic: item.italic,
          text_align: item.textAlign || 'center',
        }
      : {}),
  })),
});

export const buildBookPrintProfile = (
  book: Book,
  form: BookDesignFormState
): Record<string, unknown> => {
  const settings = buildBookDesignSettings(form);
  const provider = form.publishingProvider.trim() || 'amazon_kdp';
  return {
    format: form.exportType,
    provider,
    pdf_profile: 'pdf_x_ready',
    page_size: settings.page_size,
    trim_size: settings.page_size,
    bleed_cm: 0.3,
    safe_area_cm: 0.5,
    margins: settings.margins,
    color_profile: 'printer_default_cmyk',
    font_embedding_required: true,
    minimum_image_dpi: 300,
    language: form.language,
    text_direction: form.textDirection,
    source_book_status: book.status,
  };
};
